import { Amplify } from 'aws-amplify';
import { signIn, signUp, confirmSignUp, confirmSignIn, resendSignUpCode, resetPassword, confirmResetPassword, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import AuthenticationError from 'src/validation/exception_messages/AuthenticationError';

// Enum for Cognito error codes from errorMessages keys
export enum CognitoErrorCode {
    UserNotFoundException = 'UserNotFoundException',
    NotAuthorizedException = 'NotAuthorizedException',
    UserNotConfirmedException = 'UserNotConfirmedException',
    TooManyRequestsException = 'TooManyRequestsException',
    UsernameExistsException = 'UsernameExistsException',
    InvalidPasswordException = 'InvalidPasswordException',
    InvalidParameterException = 'InvalidParameterException',
    CodeMismatchException = 'CodeMismatchException',
    ExpiredCodeException = 'ExpiredCodeException',
    LimitExceededException = 'LimitExceededException',
    InvalidUserPoolConfigurationException = 'InvalidUserPoolConfigurationException',
    NetworkError = 'NetworkError',
    NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED',
    MFA_REQUIRED = 'MFA_REQUIRED',
}

export const COGNITO_ERROR_CODE_TO_MESSAGE: { [key: string]: string } = {
    [CognitoErrorCode.UserNotFoundException]: 'User not found. Please check your email address.',
    [CognitoErrorCode.NotAuthorizedException]: 'Incorrect password. Please try again.',
    [CognitoErrorCode.UserNotConfirmedException]: 'Please confirm your email address before logging in.',
    [CognitoErrorCode.TooManyRequestsException]: 'Too many login attempts. Please try again later.',
    [CognitoErrorCode.UsernameExistsException]: 'An account with this email already exists.',
    [CognitoErrorCode.InvalidPasswordException]: 'Password does not meet requirements.',
    [CognitoErrorCode.InvalidParameterException]: 'Please enter a valid email address.',
    [CognitoErrorCode.CodeMismatchException]: 'Invalid confirmation code. Please check your email.',
    [CognitoErrorCode.ExpiredCodeException]: 'Confirmation code has expired. Please request a new one.',
    [CognitoErrorCode.LimitExceededException]: 'Attempt limit exceeded. Please try again later.',
    [CognitoErrorCode.InvalidUserPoolConfigurationException]: 'Invalid user pool configuration.',
    [CognitoErrorCode.NetworkError]: 'Network error. Please check your connection.',
    [CognitoErrorCode.NEW_PASSWORD_REQUIRED]: 'Please set a new password.',
    [CognitoErrorCode.MFA_REQUIRED]: 'MFA is required. Please contact support.'
};

interface CognitoAuthServiceProps {
    loginDetails: any;
}

class CognitoAuthService {
    private loginDetails: any;

    constructor({ loginDetails }: CognitoAuthServiceProps) {
        this.loginDetails = loginDetails;
        this.configureAmplify();
    }

    private configureAmplify() {
        Amplify.configure({
            Auth: {
                Cognito: {
                    userPoolId: 'eu-west-1_pJwJrmIK8',
                    userPoolClientId: this.loginDetails.CLIENT_ID
                }
            }
        });
    }

    async getToken(backendBaseApiUrl: string = ''): Promise<{ id_token: string, access_token: string } | null> {
        // Path 1: Cognito (email/password) — Amplify handles refresh automatically
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            const accessToken = session.tokens?.accessToken?.toString();
            if (idToken && accessToken) {
                return { id_token: idToken, access_token: accessToken };
            }
        } catch {
            // Fall through to SSO token check
        }

        // Path 2: HomeIndex tokens (Google SSO) — refresh manually if expired
        if (this.isSsoTokenExpired() && backendBaseApiUrl) {
            const refreshed = await this.refreshSsoTokens(backendBaseApiUrl);
            if (!refreshed) {
                this.clearSsoTokens();
                return null;
            }
        }

        return this.getSsoTokens();
    }

    storeSsoTokens(idToken: string, accessToken: string, refreshToken: string): void {
        localStorage.setItem('sso_id_token', idToken);
        localStorage.setItem('sso_access_token', accessToken);
        localStorage.setItem('sso_refresh_token', refreshToken);
    }

    private isSsoTokenExpired(): boolean {
        const accessToken = localStorage.getItem('sso_access_token');
        if (!accessToken) return true;
        try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            return payload.exp * 1000 < Date.now();
        } catch {
            return true;
        }
    }

    private async refreshSsoTokens(backendBaseApiUrl: string): Promise<boolean> {
        const refreshToken = localStorage.getItem('sso_refresh_token');
        if (!refreshToken) return false;
        try {
            const response = await fetch(`${backendBaseApiUrl}/refresh_token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });
            if (!response.ok) return false;
            const data = await response.json();
            this.storeSsoTokens(data.id_token, data.access_token, data.refresh_token);
            return true;
        } catch {
            return false;
        }
    }

    getSsoTokens(): { id_token: string, access_token: string } | null {
        const idToken = localStorage.getItem('sso_id_token');
        const accessToken = localStorage.getItem('sso_access_token');
        if (idToken && accessToken) {
            return { id_token: idToken, access_token: accessToken };
        }
        return null;
    }

    clearSsoTokens(): void {
        localStorage.removeItem('sso_id_token');
        localStorage.removeItem('sso_access_token');
        localStorage.removeItem('sso_refresh_token');
    }

    async signIn(email: string, password: string): Promise<any> {
        try {
            const currentUser = await getCurrentUser().catch(() => null);
            if (currentUser) {
                await signOut({ global: true });
            }

            const result = await signIn({ username: email, password: password });

            if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
                throw new Error('UserNotConfirmedException');
            }
            if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                return { success: false, newPasswordRequired: true };
            }
            if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE' ||
                result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
                throw new Error('MFA_REQUIRED');
            }

            return { success: true, user: result };
        } catch (error: any) {
            console.error('Sign in error:', error);
            throw this.mapCognitoError(error);
        }
    }

    async completeNewPassword(newPassword: string): Promise<any> {
        try {
            const result = await confirmSignIn({ challengeResponse: newPassword });
            return { success: true, user: result };
        } catch (error: any) {
            console.error('Complete new password error:', error);
            throw this.mapCognitoError(error);
        }
    }

    async signUp(email: string, password: string): Promise<any> {
        try {
            const result = await signUp({
                username: email,
                password: password,
                options: { userAttributes: { email: email } }
            });
            return { success: true, result };
        } catch (error: any) {
            console.error('Sign up error:', error);
            throw this.mapCognitoError(error);
        }
    }

    async confirmSignUp(email: string, code: string): Promise<any> {
        try {
            await confirmSignUp({ username: email, confirmationCode: code });
            return { success: true };
        } catch (error: any) {
            console.error('Confirm sign up error:', error);
            throw this.mapCognitoError(error);
        }
    }

    async resendConfirmationCode(email: string): Promise<any> {
        try {
            await resendSignUpCode({ username: email });
            return { success: true };
        } catch (error: any) {
            console.error('Resend confirmation code error:', error);
            throw this.mapCognitoError(error);
        }
    }

    async forgotPassword(email: string): Promise<any> {
        try {
            await resetPassword({ username: email });
            return { success: true };
        } catch (error: any) {
            console.error('Forgot password error:', error);
            throw this.mapCognitoError(error);
        }
    }

    async resetPassword(email: string, code: string, newPassword: string): Promise<any> {
        try {
            await confirmResetPassword({ username: email, confirmationCode: code, newPassword: newPassword });
            return { success: true };
        } catch (error: any) {
            console.error('Reset password error:', error);
            throw this.mapCognitoError(error);
        }
    }

    async signOut(): Promise<void> {
        try {
            this.clearSsoTokens();
            await signOut({ global: true });
        } catch (error) {
            console.error('Sign out error:', error);
        }
    }

    async getCurrentUser(): Promise<any> {
        try {
            return await getCurrentUser();
        } catch (error) {
            return null;
        }
    }

    async isAuthenticated(): Promise<boolean> {
        try {
            const user = await getCurrentUser();
            if (user) return true;
        } catch {
            // Fall through to SSO check
        }
        return this.getSsoTokens() !== null;
    }

    getCognitoErrorCode(error: any): CognitoErrorCode {
        if (Object.values(CognitoErrorCode).includes(error.code)) return error.code;
        if (Object.values(CognitoErrorCode).includes(error.message)) return error.message;
        if (Object.values(CognitoErrorCode).includes(error.name)) return error.name;
        if (!error.code) return error.message;
        return error.code;
    }

    private mapCognitoError(error: any): Error {
        const cognitoErrorCode = this.getCognitoErrorCode(error);
        const message = COGNITO_ERROR_CODE_TO_MESSAGE[cognitoErrorCode] || error.message || 'An unexpected error occurred.';
        return new AuthenticationError(message, cognitoErrorCode);
    }
}

export default CognitoAuthService;
